import log from './log';
import Mustache from 'mustache';

export default class Native {

  /**
   * Constructor.
   *
   * @param {HTMLElement} context
   */
  constructor(context) {
    this.backfillArticles = [];   // Pool of articles to use for backfill.
    this.context = context;
    this.elToBackfill = [];       // Elements to backfill with with articles.
    this.nativeArticles = [];     // Pool of native articles to use.
    this.nativeTemplate = '';
  }

  /**
   * Fetch the template to use for both rendering the native articles
   * and the fallbacks.
   *
   * @returns {Promise}
   */
  getNativeTemplate() {
    return new Promise((resolve, reject) => {
      fetch('/templates/native.html')
        .then((resp) => {
          return resp.text();
        })
        .then((text) => {
          // Prepare template for use.
          this.nativeTemplate = $(text).filter('#native-ad-template').html();
          Mustache.parse(this.nativeTemplate); // optional, speeds up uses
          resolve();
        })
        .catch((err) => {
          log('Could not fetch native template.', err);
          reject(err);
        });
    });
  }

  /**
   * Gets the native articles from the server (in parallell) and renders
   * them as soon as possible. Once all slots are rendered, the returned
   * promise will resolve.
   *
   * @returns {Promise}
   */
  showNativeArticles() {
    return new Promise((resolve, reject) => {
      // First detect available slots.
      const slots = this.detectUniqueSlots();

      // Once all native articles are done fetching AND rendering, we resolve our returned promise.
      let renderingDone = [];

      // Fetch native data - only one call should be made per siteId-Tag combo.
      log('Native slots:', slots);

      slots.forEach((slot) => {
        renderingDone.push(new Promise((resolve, reject) => {
          this.getNativeArticles(slot)
            .then((articles) => {
              // Store the articles in our article pool for the slot.
              this.nativeArticles[slot.cacheKey] = articles;

              // As soon as we get a result, render slots relying on this data.
              this.renderNativeArticles(slot);
              resolve();
            })
            .catch((err) => {
              log('Could not render native article.', err);
              reject(err);
            });
        }));
      });

      // When all data is fetched and redered, we resolve our promise.
      Promise.all(renderingDone)
        .then(() => resolve())
        .catch(err => reject(err));
    });
  }

  /**
   * Fetches native articles from the server for this specific slot.
   *
   * @param {object} slot
   * @returns {Promise}
   */
  getNativeArticles(slot) {
    return new Promise((resolve, reject) => {
      const url = `${process.env.API_BASE_URL}article/?query=`
        + `visibility_status:P AND tag:(${slot.tag}) AND nativeadstart_date:[* TO NOW] AND nativeadend_date:[NOW TO *] AND isNativeAd:1`
        + `&site_id=${slot.siteId}`;

      fetch(url, { cache: 'no-cache' })
        .then((resp) => {
          return resp.json();
        })
        .then((data) => {
          // Shuffle the data before returning it.
          resolve(this.shuffleArray(data.result));
        })
        .catch((err) => {
          log('Could not fetch native articles.', err);
          reject(err);
        });
    });
  }

  /**
   * Renders native articles relying on data from the provided slot.
   *
   * @param {object} slot
   */
  renderNativeArticles(slot) {
    $(`content-box[data-site='${slot.siteId}']content-box[data-tag='${slot.tag}']`, this.context).each((index, element) => {
      const el = $(element);
      const article = this.nativeArticles[slot.cacheKey] && this.nativeArticles[slot.cacheKey].length ? this.nativeArticles[slot.cacheKey][0] : false;

      // If no native article left, remember to backfill this element.
      if (!article) {
        this.elToBackfill.push(el);
        log(`Could not find a native article for: ${slot.cacheKey}.`);
        return;
      }

      const params = {
        fields: {
          published_url: article.published_url + '?native=1'
        },
        filtered: {
          articleId: article.id,
          image: this.getImageUrl(article),
          nativeAdLabel: article.nativeAdLabel,
          title: article.title
        }
      };

      // Render and insert element.
      const rendered = Mustache.render(this.nativeTemplate, params);
      el.find('article').attr('data-id', article.id).html(rendered);

      // Remove article from the article pool.
      this.nativeArticles[slot.cacheKey].shift();
      log(`Rendered ad: ${slot.cacheKey}. Remaining ads: `, JSON.parse(JSON.stringify(this.nativeArticles[slot.cacheKey])));
    });
  }

  /**
   * Compiles an array of unique siteId/tag combinations found in the context.
   *
   * @returns {Array}
   */
  detectUniqueSlots() {
    let slots = {};
    $('.js-native-ad', this.context).each(function () {
      let el = $(this);
      let key = `${el.data('site')}-${el.data('tag')}`;
      if (!slots[key]) {
        slots[key] = {
          'siteId': el.data('site'),
          'tag': el.data('tag'),
          'cacheKey': key
        };
      }
    });
    return Object.values(slots);
  }

  /**
   * Are there any slots in need of backfilling?
   *
   * @returns {boolean}
   */
  isBackfillNeeded() {
    return this.elToBackfill.length > 0;
  }

  /**
   * Gets the backfill articles from the server (in parallell per site) and renders
   * them as soon as possible. Once all slots are rendered, the returned
   * promise will resolve.
   *
   * @returns {Promise}
   */
  showBackfillArticles() {
    return new Promise((resolve, reject) => {

      // Map/reduce how many articles we need from each fallback site.
      const backfillsNeeded = this.elToBackfill
        .map(el => +el.attr('data-backfill-site'))
        .reduce(function (prev, cur) {
          prev[cur] = (prev[cur] || 0) + 1;
          return prev;
        }, {});

      // Once all backfill articles are done fetching AND rendering, we resolve our returned promise.
      let renderingDone = [];

      for (let [siteId, limit] of Object.entries(backfillsNeeded)) {
        if (+siteId === 0) { // Backfill disabled.
          continue;
        }

        renderingDone.push(new Promise((resolve, reject) => {
          this.getBackfillArticles(siteId, limit)
            .then((articles) => {
              // Store the backfill articles in our article pool for the site id.
              this.backfillArticles[siteId] = articles;

              // As soon as we get a result, render slots relying on this data.
              this.renderBackfillArticles(+siteId);
              resolve();
            })
            .catch((err) => {
              log(`Could not render backfill articles for siteId: ${siteId}.`, err);
              reject(err);
            });
        }));
      }

      // When all data is fetched and redered, we resolve our promise.
      Promise.all(renderingDone)
        .then(() => resolve())
        .catch(err => reject(err));
    });
  }

  /**
   * Fetch backfill articles querying from the oldest article date on the page,
   * then randomizing a date/time from that date and 7 days earlier.
   *
   * If the site is less than 7 days old, this might result in no articles found.
   *
   * @param {int} siteId
   * @param {int} limit
   * @returns {Promise}
   */
  getBackfillArticles(siteId, limit) {
    return new Promise((resolve, reject) => {
      // Get a random date to use in the query.
      const rangeStart = new Date($(this.elToBackfill[0]).attr('data-query-date')); // Get oldest article on page (supplied via slot attribute from filter).
      const rangeEnd = new Date();
      rangeEnd.setDate(rangeStart.getDate() - 7); // 7 days earlier than rangeStart.
      const randomDate = this.getRandomDate(rangeStart, rangeEnd);

      const published = 'published:[* ' + randomDate.toLocaleDateString('sv-SE') + 'T' + randomDate.toLocaleTimeString('sv-SE') + 'Z]';

      const url = `${process.env.API_BASE_URL}article/?query=`
        + `visibility_status:P ${published}`
        + `&site_id=${siteId}`
        + `&orderBy=published&sort=desc`
        + `&limit=${limit}`;

      fetch(url, { cache: 'no-cache' })
        .then((resp) => {
          return resp.json();
        })
        .then((data) => {
          // Shuffle the data before returning it.
          resolve(this.shuffleArray(data.result));
        })
        .catch((err) => {
          log('Could not fetch backfill articles.', err);
          reject(err);
        });
    });
  }

  /**
   * Renders the backfill articles on the slots that was missing a native article.
   *
   * @param {int} siteId
   */
  renderBackfillArticles(siteId) {
    this.elToBackfill.forEach(el => {
      if (+el.attr('data-backfill-site') === siteId) {
        const article = this.backfillArticles[siteId] && this.backfillArticles[siteId].length ? this.backfillArticles[siteId][0] : false;

        // If no backfill articles left.
        if (!article) {
          log(`Could not find a backfill article for:`, el);
          return;
        }
        const linkInSectionTagPlacement = article.verticalHost ? this.condenseVerticalSlug(article.verticalHost) : article.section_tag;
        const params = {
          fields: {
            published_url: article.published_url
          },
          filtered: {
            articleId: article.id,
            image: this.getImageUrl(article),
            sectionTag: linkInSectionTagPlacement,
            title: article.title
          }
        };

        // Render and insert element.
        const rendered = Mustache.render(this.nativeTemplate, params);
        el.find('article').attr('data-id', article.id).html(rendered);

        // Remove article from pool.
        this.backfillArticles[siteId].shift();
        log(`Rendered backfill article ${article.id}.`);
      }
    });
  }

  /**
   * Creates a random Date object (date + time) between the provided start and end dates.
   *
   * @param {Date} start
   * @param {Date} end
   * @returns {Date}
   */
  getRandomDate(start, end) {
    // get the difference between the 2 dates, multiply it by 0-1,
    // and add it to the start date to get a new date
    const diff = end.getTime() - start.getTime();
    const new_diff = diff * Math.random();
    const date = new Date(start.getTime() + new_diff);
    return date;
  }

  /**
   * Shuffles array in place.
   *
   * @param {Array} a An array containing the items.
   * @returns {Array}
   */
  shuffleArray(a) {
    if (!Array.isArray(a)) {
      return a;
    }

    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  /**
   * Logic to get image URL to use.
   *
   * @param {Object} article
   */
  getImageUrl(article) {
    // There are odd cases where frontCropUrl will exist, but there is actually no real
    // crop data in there. For these cases we must use fallback size, else it might take
    // a standing image and allow that to be as big as it is.
    if (article.frontCropUrl && article.frontCropUrl.includes('height') && article.frontCropUrl.includes('width')) {
      return window.imageServer + article.frontCropUrl;
    }

    // Fallback.
    return `${window.imageServer}?imageId=${article.image}&x=0&y=0&cropw=0&croph=0&width=642&height=337`;
  }

  condenseVerticalSlug(verticalSlug) {
    const name = verticalSlug
      .replace('bebis-och-smabarn', 'Bebis & Småbarn')
      .replace('tonaringar', 'Tonåringar');
    return name;
  }
}
